import {
  CoachSessionRemarkV2LabelMap,
  coachSessionRemarkV2Schema,
  lessonKindSchema,
  LessonKindToLabel,
  meetingStatusSchema,
  studentYearSchema,
} from '@airreading/common/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ReactNode, useCallback, useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';

import { InfoPage } from '@/components/info-page.js';
import { SessionDateDivider } from '@/components/session/session-date-divider.js';
import { EntityTableGroupBy, EntityTableInfiniteScroll } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu.js';
import { Button } from '@/components/ui/button.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { lessonTypeaheadSearch, useLessonById, useOrgOptions, useSchoolOptions } from '@/lib/api-call-hooks.js';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { useInfiniteScrollQuery } from '@/lib/infinite-query-utils.js';
import { INFINITE_SCROLL_PAGE_SIZE, paginationRequestSchema } from '@/lib/pagination.js';
import { validateRouteRole } from '@/lib/routing.js';
import { pluralize } from '@/lib/string-utils.js';
import { getStudentYearOptions } from '@/lib/student-utils.js';
import { groupByStartDate, StartDateGroupKey } from '@/lib/table-grouping-utils.js';
import { BulkOpenSubRequests } from '@/routes/_layout/sessions/-components/bulk-open-sub-requests.js';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions.js';
import { CreateSessionModal } from '@/routes/_layout/sessions/-components/create-session-modal.js';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions.js';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table.js';
import { sessionsTabs } from '@/routes/_layout/sessions/-components/tabs.js';
import { FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import { AdminSession } from '@airreading/common/data';
import { meetingStatusesFilterOption } from '@/lib/form-utils.js';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { z } from 'zod';

export const Route = createFileRoute('/_layout/sessions/future/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: FutureSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    orgs: z.string().array().optional(),
    schools: z.string().array().optional(),
    grades: z.array(studentYearSchema).optional(),
    lessons: z.string().array().optional(),
    sessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    lessonKinds: z.array(lessonKindSchema).optional(),
    meetingStatuses: z.array(meetingStatusSchema).optional(),
  }),
});

const nowInCST = Temporal.Now.zonedDateTimeISO(FrequentTimeZones.UsCentral);
const today = nowInCST.startOfDay().toInstant().toString();

function useFutureSessionsModel() {
  const queryClient = useQueryClient();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      sessionRemarksV2: [[selectedSessionRemarksV2, activeSelectedSessionRemarksV2], setSelectedSessionRemarksV2],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
      lessonKinds: [[selectedLessonKinds, activeSelectedLessonKinds], setSelectedLessonKinds],
      meetingStatuses: [[selectedMeetingStatuses, activeSelectedMeetingStatuses], setSelectedMeetingStatuses],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(
    Route.id,
    [
      'query',
      'orgs',
      'schools',
      'grades',
      'lessons',
      'sessionRemarksV2',
      'startDate',
      'endDate',
      'lessonKinds',
      'meetingStatuses',
    ],
    ['grades', 'lessons', 'sessionRemarksV2', 'lessonKinds', 'meetingStatuses'],
  );

  const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);

  const requestQuery = useCallback(
    (nextPageIndex: number, nextPageSize: number) =>
      getAdminSessions({
        pageIndex: nextPageIndex,
        pageSize: nextPageSize,
        filters: {
          startOnOrAfter: selectedStartDate
            ? Temporal.PlainDate.from(selectedStartDate)
                .toZonedDateTime(FrequentTimeZones.UsCentral)
                .startOfDay()
                .toInstant()
                .toString()
            : today,
          startOnOrBefore: endPlanDate
            ? endPlanDate
                .toZonedDateTime(FrequentTimeZones.UsCentral)
                .add({ days: 1 })
                .startOfDay()
                .subtract({ milliseconds: 1 })
                .toInstant()
                .toString()
            : undefined,
          query: activeQuery,
          orgs: activeSelectedOrgs,
          schools: activeSelectedSchools,
          grades: activeSelectedGrades,
          lessons: activeSelectedLessons,
          sessionRemarksV2: activeSelectedSessionRemarksV2,
          lessonKinds: activeSelectedLessonKinds,
          meetingStatuses: activeSelectedMeetingStatuses,
        },
        sortBy: ['start-asc'],
      }),
    [
      selectedStartDate,
      endPlanDate,
      activeQuery,
      activeSelectedOrgs,
      activeSelectedSchools,
      activeSelectedGrades,
      activeSelectedLessons,
      activeSelectedSessionRemarksV2,
      activeSelectedLessonKinds,
      activeSelectedMeetingStatuses,
    ],
  );

  const allSchoolOptions = useSchoolOptions();
  const allOrgOptions = useOrgOptions();
  const fetchLessonById = useLessonById();

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[250px] lg:w-[350px] xl:w-[400px] 2xl:w-[450px]"
          placeholder="Search sessions by student, teacher, email or lesson name"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="District"
          options={allOrgOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date"
        />
        <TableMoreFiltersMenu
          activeFiltersCount={activeFiltersCount}
          filterItems={[
            {
              title: 'Grade',
              filterComponent: (
                <TableFilterDropdown
                  title="Grade"
                  options={getStudentYearOptions()}
                  selectedValues={selectedGrades ?? []}
                  onChange={setSelectedGrades}
                />
              ),
            },
            {
              title: 'Lesson type',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson type"
                  options={lessonKindSchema.options.map((type) => ({
                    label: LessonKindToLabel[type],
                    value: type,
                  }))}
                  selectedValues={selectedLessonKinds ?? []}
                  onChange={setSelectedLessonKinds}
                />
              ),
            },
            {
              title: 'Lesson name',
              filterComponent: (
                <TableFilterDropdown
                  title="Lesson name"
                  options={lessonTypeaheadSearch(queryClient)}
                  selectedValues={selectedLessons ?? []}
                  onChange={setSelectedLessons}
                  getOptionSearchFn={fetchLessonById}
                />
              ),
            },
            {
              title: 'Remarks',
              filterComponent: (
                <TableFilterDropdown
                  title="Remarks"
                  options={coachSessionRemarkV2Schema.options.map((type) => ({
                    label: CoachSessionRemarkV2LabelMap[type],
                    value: type,
                  }))}
                  selectedValues={selectedSessionRemarksV2 ?? []}
                  onChange={setSelectedSessionRemarksV2}
                />
              ),
            },
            {
              title: 'Meeting status',
              filterComponent: (
                <TableFilterDropdown
                  title="Meeting status"
                  options={meetingStatusesFilterOption}
                  selectedValues={selectedMeetingStatuses ?? []}
                  onChange={setSelectedMeetingStatuses}
                />
              ),
            },
          ]}
        />

        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      allOrgOptions,
      selectedOrgs,
      setSelectedOrgs,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedGrades,
      setSelectedGrades,
      selectedStartDate,
      selectedEndDate,
      selectedLessonKinds,
      setSelectedLessonKinds,
      queryClient,
      selectedLessons,
      setSelectedLessons,
      fetchLessonById,
      selectedSessionRemarksV2,
      setSelectedSessionRemarksV2,
      selectedMeetingStatuses,
      setSelectedMeetingStatuses,
      resetFilters,
      setQuery,
      setSelectedStartDate,
      setSelectedEndDate,
      activeFiltersCount,
    ],
  );

  const topBarRightComponents = <CreateSessionModal trigger={<Button>New session</Button>} />;

  const displayTimeZone = FrequentTimeZones.UsCentral;

  const sessionGroupBy = useMemo<EntityTableGroupBy<AdminSession, StartDateGroupKey>>(
    () => ({
      getGroupKey: (row) => groupByStartDate(row.startInstant, displayTimeZone),
      renderDivider: (groupKey, rowCount) => <SessionDateDivider groupKey={groupKey} rowCount={rowCount} />,
    }),
    [displayTimeZone],
  );

  return {
    pageIndex,
    pageSize,
    onPaginationChange,
    requestQuery,
    filterLeftComponents,
    topBarRightComponents,
    displayTimeZone,
    sessionGroupBy,
  };
}

type FutureSessionsModel = ReturnType<typeof useFutureSessionsModel>;

function FutureSessionsView({
  model,
  sessions,
  rowCount,
  isLoading,
  hasError,
  refetch,
  paginationState,
  infiniteScroll,
}: {
  model: FutureSessionsModel;
  sessions: AdminSession[];
  rowCount: number;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
  paginationState?: {
    pageIndex: number;
    pageSize: number;
    onPaginationChange: FutureSessionsModel['onPaginationChange'];
  };
  infiniteScroll?: EntityTableInfiniteScroll;
}): ReactNode {
  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <BulkOpenSubRequests
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Open sub request', 'Open sub requests')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  return (
    <InfoPage headerText="Sessions" tabs={sessionsTabs('Future sessions')}>
      <SessionsTable
        sessions={sessions}
        hasError={hasError}
        isLoading={isLoading}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            ...(paginationState
              ? { pagination: { pageIndex: paginationState.pageIndex, pageSize: paginationState.pageSize } }
              : {}),
            rowSelection,
          },
          onRowSelectionChange,
          getRowId,
          ...(paginationState ? { onPaginationChange: paginationState.onPaginationChange } : {}),
        }}
        displayTimeZone={model.displayTimeZone}
        filters={{ filterLeftComponents: model.filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={model.topBarRightComponents}
        from="future"
        groupBy={model.sessionGroupBy}
        infiniteScroll={infiniteScroll}
      />
    </InfoPage>
  );
}

function PagedFutureSessions() {
  const model = useFutureSessionsModel();
  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(model.requestQuery(model.pageIndex, model.pageSize));
  useErrorToast(error, 'Error loading sessions. Please try again.');
  const sessions = useMemo(() => sessionsResp?.adminSessions ?? EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total ?? 0;

  return (
    <FutureSessionsView
      model={model}
      sessions={sessions}
      rowCount={rowCount}
      isLoading={isPending}
      hasError={!!error}
      refetch={refetch}
      paginationState={{
        pageIndex: model.pageIndex,
        pageSize: model.pageSize,
        onPaginationChange: model.onPaginationChange,
      }}
    />
  );
}

function InfiniteFutureSessions() {
  const model = useFutureSessionsModel();
  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteScrollQuery(model.requestQuery, (result) => result.adminSessions, INFINITE_SCROLL_PAGE_SIZE);
  useErrorToast(error, 'Error loading sessions. Please try again.');
  const sessions = useMemo(
    () => sessionsResp?.pages.flatMap((page) => page.adminSessions) ?? EmptyArray,
    [sessionsResp],
  );
  const rowCount = sessionsResp?.pages[0]?.total ?? 0;

  return (
    <FutureSessionsView
      model={model}
      sessions={sessions}
      rowCount={rowCount}
      isLoading={isPending}
      hasError={!!error}
      refetch={refetch}
      infiniteScroll={{
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        pageSize: INFINITE_SCROLL_PAGE_SIZE,
      }}
    />
  );
}

function FutureSessionsIndex() {
  const infiniteScrollEnabled = useFeatureFlag('entity-table-infinite-scroll', false);
  return infiniteScrollEnabled ? <InfiniteFutureSessions /> : <PagedFutureSessions />;
}
